import { HttpClient } from '../httpClient';
import { BaseResponse, DataResponse } from '../responses/baseResponse';
import homeDistrictRequest from '../requests/homeDistrictRequest';
import { selectOption } from '../../types/selectOption';
import HomeDistrict from '../models/homeDistrict';

type option = { label: string; value: number; };
const httpClient = new HttpClient();

/**
 * Retrieves the home districts data from the server.
 *
 * @return {Promise<DataResponse<HomeDistrict[]>>} A promise that resolves with the home districts data.
 */
export const getHomeDistricts = async () => {
  return httpClient.get<DataResponse<HomeDistrict[]>>({
    url: '/v1/HomeDistricts',
    requiresToken: true,
  });
};

/**
 * Retrieves the home districts by the given state ID.
 *
 * @param {number} stateId - The ID of the state.
 * @return {Promise<DataResponse<HomeDistrict[]>>} A promise that resolves to a data response containing an array of home districts.
 */
export const getHomeDistrictsByStateId = async (stateId: number) => {
  return httpClient.get<DataResponse<HomeDistrict[]>>({
    url: `/v1/HomeDistricts/ByState/${stateId}`,
    requiresToken: true,
  });
};

/**
 * Retrieves the home district data for a given home district ID.
 *
 * @param {number} homeDistrictId - The ID of the home district to retrieve data for.
 * @return {Promise<DataResponse<HomeDistrict>>} A Promise that resolves to the home district data.
 */
export const getHomeDistrict = async (homeDistrictId: number) => {
  return httpClient.get<DataResponse<HomeDistrict>>({
    url: `/v1/HomeDistricts/${homeDistrictId}`,
    requiresToken: true,
  });
};


/**
 * Creates a home district.
 *
 * @param {homeDistrictRequest} homeDistrictrequest - The request object for creating a home district.
 * @return {Promise<DataResponse<HomeDistrict>>} A promise that resolves to a data response containing the created home district.
 */
export const createHomeDistrict = async (homeDistrictrequest: homeDistrictRequest) => {
  return httpClient.post<DataResponse<HomeDistrict>>({
    url: '/v1/admin/HomeDistricts',
    payload: homeDistrictrequest,
    requiresToken: true,
  });
};


/**
 * Updates the home district with the specified ID.
 *
 * @param {number} homeDistrictId - The ID of the home district to update.
 * @param {homeDistrictRequest} homeDistrictrequest - The updated home district data.
 * @return {Promise<DataResponse<HomeDistrict>>} - A promise that resolves to the updated home district.
 */
export const updateHomeDistrict = async (
  homeDistrictId: number,
  homeDistrictrequest: homeDistrictRequest
) => {
  return httpClient.put<DataResponse<HomeDistrict>>({
    url: `/v1/admin/HomeDistricts/${homeDistrictId}`,
    payload: homeDistrictrequest,
    requiresToken: true,
  });
};

/**
 * Deletes a home district with the specified ID.
 *
 * @param {number} homeDistrictId - The ID of the home district to delete.
 * @return {Promise<DataResponse<HomeDistrict>>} A promise that resolves to the deleted home district.
 */
export const deleteHomeDistrict = async (homeDistrictId: number) => {
  return httpClient.post<DataResponse<HomeDistrict>>({
    url: `/v1/admin/HomeDistricts/${homeDistrictId}/Delete`,
    requiresToken: true,
  });
};

/**
 * Retrieves the options for the home districts based on the specified state ID.
 *
 * @param {number} stateId - The ID of the state to filter the options by (optional).
 * @return {Promise<DataResponse<selectOption[]>>} A promise that resolves to the response containing the options for the home districts.
 */
export const getHomeDistrictOptions = async (stateId?: number) => {
  return httpClient.get<DataResponse<selectOption[]>>({
    url: `/v1/HomeDistricts/Options`,
    requiresToken: true,
  });
};

/**
 * Retrieves the home district options based on the provided state ID.
 *
 * @param {number} stateId - The ID of the state. (optional)
 * @return {Promise<DataResponse<option[]>>} A Promise that resolves to the home district options.
 */
export const getHomeDistrictOptionsByStateId = async (stateId?: number) => {
  return httpClient.get<DataResponse<option[]>>({
    url: `/v1/HomeDistricts/Options/ByState/${Number.isNaN(stateId) ? 0 : stateId}`,
    requiresToken: true,
  });
};